import { useEffect, useMemo, useState } from 'react';
import { FileText, Plus, ShieldCheck, UserMinus, Link2, Radio, RefreshCw } from 'lucide-react';
import { useStore, isAdminPk, boardPkOf } from '@/relatos/store';
import { TimeAgo, displayName, OnlinePill, shortNpub } from './bits';
import { bbcodeToText } from '@/lib/bbcode';
import { UploadButtons } from './upload-buttons';

function NewTopic({ boardId, onDone }: { boardId: string; onDone: () => void }) {
  const { postThread } = useStore();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await postThread(boardId, subject.trim(), body);
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  return (
    <div className="rl-panel">
      <div className="rl-h2">New topic</div>
      <div className="rl-field">
        <label htmlFor="nt-subject">Subject</label>
        <input id="nt-subject" className="rl-input" value={subject} onChange={e => setSubject(e.target.value)} maxLength={120} />
      </div>
      <div className="rl-field">
        <label htmlFor="nt-body">Message (BBCode supported)</label>
        <div className="rl-toolbar"><UploadButtons onInsert={s => setBody(b => b + s)} boardId={boardId} /></div>
        <textarea id="nt-body" className="rl-textarea" value={body} onChange={e => setBody(e.target.value)} />
      </div>
      <button className="rl-btn rl-btn-primary" disabled={busy || !subject.trim() || !body.trim()} onClick={go}>
        {busy ? 'Encrypting & posting…' : 'Post topic'}
      </button>
      {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
    </div>
  );
}

function AdminPanel({ boardId }: { boardId: string }) {
  const { boards, data, makeInvite, removeMember, publishRelayList, signer, profiles } = useStore();
  const m = boards[boardId];
  const [npub, setNpub] = useState('');
  const [ttl, setTtl] = useState(72);
  const [invite, setInvite] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [relayText, setRelayText] = useState((m?.relays ?? []).join('\n'));
  const [relayBusy, setRelayBusy] = useState(false);
  const [relayMsg, setRelayMsg] = useState<string | null>(null);

  if (!m || m.role !== 'admin') return null;
  const roster = Object.keys(data[boardId]?.roster ?? {});

  const gen = async () => {
    setErr(null); setInvite(null);
    try {
      const token = await makeInvite(boardId, npub, ttl);
      setInvite(`${location.origin}${location.pathname}#/join/${token}`);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const kick = async (pk: string) => {
    setErr(null);
    try { await removeMember(boardId, pk); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const pubRelays = async () => {
    setRelayBusy(true); setRelayMsg(null); setErr(null);
    const list = relayText.split('\n').map(s => s.trim()).filter(s => s.startsWith('wss://') || s.startsWith('ws://'));
    try {
      await publishRelayList(boardId, [...new Set(list)]);
      setRelayMsg('Relay list published.');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setRelayBusy(false);
  };

  return (
    <>
      <div className="rl-cat"><span>Board administration</span><ShieldCheck size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        <div className="rl-h2"><Link2 size={12} aria-hidden style={{ verticalAlign: -2 }} /> Invite a member</div>
        <div className="rl-field">
          <label htmlFor="inv-npub">Their pubkey (npub… or nprofile…) — the invite is bound to it</label>
          <input id="inv-npub" className="rl-input rl-mono" value={npub} onChange={e => setNpub(e.target.value)} placeholder="npub1…" />
        </div>
        <div className="rl-field" style={{ maxWidth: 180 }}>
          <label htmlFor="inv-ttl">Expires after (hours)</label>
          <input id="inv-ttl" className="rl-input" type="number" min={1} value={ttl} onChange={e => setTtl(Number(e.target.value) || 72)} />
        </div>
        <button className="rl-btn" onClick={gen} disabled={!npub.trim()}>Generate invite link</button>
        {invite && (
          <>
            <div className="rl-invite-out">{invite}</div>
            <button className="rl-btn rl-btn-sm" style={{ marginTop: 6 }} onClick={() => navigator.clipboard.writeText(invite)}>Copy link</button>
          </>
        )}

        <hr className="rl-hr" />
        <div className="rl-h2"><UserMinus size={12} aria-hidden style={{ verticalAlign: -2 }} /> Members seen</div>
        {roster.length === 0 && <p className="rl-small">No members seen yet — roster builds from join and post events.</p>}
        {roster.map(pk => {
          const me = boardPkOf(m) ?? signer?.pubkey;
          return (
            <div key={pk} className="rl-actions" style={{ marginBottom: 4 }}>
              <span className="rl-mono rl-small">{profiles[pk]?.name ?? shortNpub(pk)}</span>
              {isAdminPk(m, pk) && <span className="rl-pill">admin</span>}
              {pk === me && <span className="rl-pill">you</span>}
              {!isAdminPk(m, pk) && pk !== me && (
                <button className="rl-btn rl-btn-sm rl-btn-danger" onClick={() => kick(pk)}>
                  Remove &amp; rotate epoch
                </button>
              )}
            </div>
          );
        })}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Removal rotates the epoch key and re-encrypts board metadata. The removed member keeps
          access to posts from before their removal (documented tradeoff) but can never read
          anything posted after.
        </p>

        <hr className="rl-hr" />
        <div className="rl-h2"><Radio size={12} aria-hidden style={{ verticalAlign: -2 }} /> Board relays</div>
        <div className="rl-field">
          <label htmlFor="rl-relays">Extra relays for this board — one wss:// URL per line</label>
          <textarea id="rl-relays" className="rl-textarea rl-mono" rows={4} value={relayText}
                    onChange={e => setRelayText(e.target.value)} placeholder="wss://relay.example.com" />
        </div>
        <button className="rl-btn" onClick={pubRelays} disabled={relayBusy}>
          {relayBusy ? 'Publishing…' : 'Publish relay list'}
        </button>
        {relayMsg && <p className="rl-small" style={{ marginTop: 6 }}>{relayMsg}</p>}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Signed and encrypted under the current epoch key; members apply it automatically and keep
          posting even if a default relay dies. The built-in defaults always stay in the mix.
        </p>
        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
      </div>
    </>
  );
}

export default function BoardView({ boardId }: { boardId: string }) {
  const { boards, data, profiles, ensureProfiles, signer, publicBoards, joinPublicBoard, requestResync } = useStore();
  const m = boards[boardId];
  const d = data[boardId];
  const [composing, setComposing] = useState(false);
  const [joinErr, setJoinErr] = useState<string | null>(null);
  const [resyncMsg, setResyncMsg] = useState<string | null>(null);

  // Direct link to a public board? Auto-join — no invite needed.
  useEffect(() => {
    if (!m && signer && publicBoards[boardId]) {
      joinPublicBoard(boardId).catch(e => setJoinErr(e instanceof Error ? e.message : String(e)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, !!m, !!signer, !!publicBoards[boardId]]);

  const threads = useMemo(() => {
    const posts = d?.posts ?? [];
    const deleted = d?.deleted ?? {};
    const roots = posts.filter(p => {
      if (p.payload.kind !== 'thread') return false;
      // hide threads deleted by their author or the board admin
      const by = deleted[p.ev.id];
      return !(by && (by === p.ev.pubkey || (m && isAdminPk(m, by))));
    });
    const replyCount: Record<string, number> = {};
    const lastActivity: Record<string, { ts: number; by: string }> = {};
    for (const p of posts) {
      const root = p.payload.kind === 'thread' ? p.ev.id : p.payload.root!;
      replyCount[root] = (replyCount[root] ?? 0) + (p.payload.kind === 'reply' ? 1 : 0);
      const cur = lastActivity[root];
      if (!cur || p.ev.created_at > cur.ts) lastActivity[root] = { ts: p.ev.created_at, by: p.ev.pubkey };
    }
    return roots
      .map(r => ({ root: r, replies: replyCount[r.ev.id] ?? 0, last: lastActivity[r.ev.id] }))
      .sort((a, b) => (b.last?.ts ?? 0) - (a.last?.ts ?? 0));
  }, [d?.posts, d?.deleted, m]);

  useEffect(() => {
    if (d?.posts.length) ensureProfiles(d.posts.map(p => p.ev.pubkey));
  }, [d?.posts, ensureProfiles]);

  if (!m) {
    if (publicBoards[boardId] && !joinErr) {
      return <div className="rl-panel rl-empty">Opening public board…</div>;
    }
    return (
      <div className="rl-panel rl-empty">
        <p><strong>You don’t hold a key for this board.</strong></p>
        <p className="rl-small">
          If it exists, its contents are encrypted. Ask an admin for an invite bound to your npub.
        </p>
        {joinErr && <p className="rl-small" style={{ color: 'var(--rl-bad)' }}>{joinErr}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="rl-actions" style={{ justifyContent: 'space-between' }}>
        <button className="rl-btn rl-btn-primary" onClick={() => setComposing(c => !c)} disabled={!signer}>
          <Plus size={12} aria-hidden style={{ verticalAlign: -2 }} /> New topic
        </button>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          {m.role === 'member' && (
            <button
              className="rl-btn rl-btn-sm"
              title="Missed a key rotation while offline? Ask the admin for the epoch salts you lack."
              onClick={() => {
                setResyncMsg(null);
                requestResync(boardId)
                  .then(() => setResyncMsg('Resync requested — keys arrive automatically when the admin is online.'))
                  .catch(e => setResyncMsg(e instanceof Error ? e.message : String(e)));
              }}
            >
              <RefreshCw size={11} aria-hidden style={{ verticalAlign: -1 }} /> Resync keys
            </button>
          )}
          <OnlinePill online={d?.online ?? {}} />
        </span>
      </div>
      {resyncMsg && <p className="rl-small" style={{ margin: '4px 0' }}>{resyncMsg}</p>}

      {composing && <NewTopic boardId={boardId} onDone={() => setComposing(false)} />}

      <div className="rl-cat"><span>{m.meta?.name ?? 'Encrypted board…'}</span></div>
      <table className="rl-table">
        <thead>
          <tr>
            <th style={{ width: 34 }}></th>
            <th>Topics</th>
            <th className="rl-num">Replies</th>
            <th className="rl-last rl-hide-sm">Last post</th>
          </tr>
        </thead>
        <tbody>
          {threads.length === 0 && (
            <tr className="rl-row-a"><td colSpan={4}><div className="rl-empty">No topics yet. Start the first one.</div></td></tr>
          )}
          {threads.map(({ root, replies, last }, i) => (
            <tr key={root.ev.id} className={i % 2 ? 'rl-row-b' : 'rl-row-a'}>
              <td className="rl-icon"><FileText size={16} aria-hidden /></td>
              <td>
                <div className="rl-topic-title">
                  <a href={`#/t/${boardId}/${root.ev.id}`}>{root.payload.subject ?? '(no subject)'}</a>
                </div>
                <div className="rl-byline">
                  by {displayName(root.ev.pubkey, profiles)} · <TimeAgo ts={root.ev.created_at} />
                  {root.epoch !== m.currentEpoch && <span className="rl-pill" style={{ marginLeft: 4 }}>epoch {root.epoch}</span>}
                  {' '}· {bbcodeToText(root.payload.body).slice(0, 90)}
                </div>
              </td>
              <td className="rl-num">{replies}</td>
              <td className="rl-last">
                {last && <><TimeAgo ts={last.ts} /><br />by {displayName(last.by, profiles)}</>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <AdminPanel boardId={boardId} />
    </div>
  );
}
